import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Steps } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { NotificationProvider, StepsProvider } from 'core/states';
import { IStep } from 'core/types';
import { IAttributesHandle } from 'quotes/components/ExportSkuPart/steps/Attributes';
import { IAttributeForm } from 'quotes/components/ExportSkuPart/types';
import { TImportQuoteActions, useImportQuoteActions } from 'quotes/hooks';
import { IAttributes, IImportVendorQuoteForm, IVendorQuote } from 'quotes/types';

import { ImportAttributes } from './steps/Attributes';
import { IImportContactHandle, ImportContact } from './steps/Contact';

enum ImportWizardStep {
  ATTRIBUTES = 'attributes',
  CONTACT = 'contact',
}

interface IProps {
  isOpen?: boolean;
  onClose?: () => void;
  quote?: IVendorQuote;
  formDefaultValues: Partial<IImportVendorQuoteForm>;
  disabled?: boolean;
  withContactStep?: boolean;
  // A first import (no existing quote being updated) starts the Selected SPA toggle on.
  isFirstImport?: boolean;
  // Adds the Vendor picker above the contact on the contact step (alternate importer, no usable
  // mapping). The contact step is its only home here, so the caller forces that step on with it.
  withVendorLocation?: boolean;
  isVendorLocked?: boolean;
  // Shows the vendor a `mapped` extractor holds, read-only, above the contact. Never posted.
  isVendorMapped?: boolean;
  // Whether the finalize will store the vendor the user picks — copy only, see the field.
  isVendorMappingRemembered?: boolean;
  // Adds the Vendor Quote Number input below the contact (the extraction found none). Like the
  // vendor, the caller forces the contact step on with it.
  withVendorQuoteNumber?: boolean;
  // The extracted number, shown but not offered for change.
  isVendorQuoteNumberLocked?: boolean;
  // Adds the optional revision input below the number.
  withVendorQuoteRevision?: boolean;
  isContactLocked?: boolean;
  // Runs on the contact step's Next. Resolve false to stay on the step (a duplicate was reported).
  onContactNext?: (data: IImportVendorQuoteForm) => Promise<boolean>;
  // Return false when the import failed: the wizard then stays on the Attributes step with the
  // typed values so Confirm can retry. Anything else closes it on the usual delay. `importActions`
  // are the finalize mutations bound to this wizard's own notification provider, so the finalize's
  // loading / error render in the drawer header while the user still looks at it.
  onConfirm?: (
    data: IImportVendorQuoteForm,
    attributes?: IAttributeForm | null,
    importActions?: TImportQuoteActions
  ) => void | boolean | Promise<void | boolean>;
}

const ImportVendorQuoteStepsContent = ({
  isOpen = false,
  onClose,
  quote,
  formDefaultValues,
  disabled = false,
  withContactStep = false,
  isFirstImport = false,
  withVendorLocation = false,
  isVendorLocked = false,
  isVendorMapped = false,
  isVendorMappingRemembered = false,
  withVendorQuoteNumber = false,
  isVendorQuoteNumberLocked = false,
  withVendorQuoteRevision = false,
  isContactLocked = false,
  onContactNext,
  onConfirm,
}: IProps) => {
  const [contactData, setContactData] = useState<IImportVendorQuoteForm | null>(null);
  const [draftAttributes, setDraftAttributes] = useState<IAttributeForm | null>(null);
  const [hasSubmittedAttributes, setHasSubmittedAttributes] = useState(false);

  const { t } = useTranslation();

  const contactRef = useRef<IImportContactHandle>(null);
  // The vendor field's own contact suggestion, kept here because Back remounts the contact step.
  const suggestedContactIdRef = useRef<number | null>(null);
  const attributesRef = useRef<IAttributesHandle>(null);

  // Bound to this wizard's own NotificationProvider (see ImportVendorQuoteSteps below), which the
  // hook that fires the finalize sits above and cannot reach by itself. Kept in a ref: the hook
  // returns a new object every render and must not re-key the memoized steps.
  const drawerImportActionsRef = useRef<TImportQuoteActions>(null);
  drawerImportActionsRef.current = useImportQuoteActions({
    position: NotificationPosition.DRAWER,
  });

  const attributesConfig: IAttributes | null = quote?.attributes ?? null;

  const handleClose = useCallback(() => {
    setContactData(null);
    setDraftAttributes(null);
    setHasSubmittedAttributes(false);
    onClose?.();
  }, [onClose]);

  const onBackFromAttributes = useCallback(() => {
    const values = attributesRef.current?.getFormValues();

    if (values) {
      setDraftAttributes(values);
    }
  }, []);

  const onNextFromContact = useCallback(async () => {
    const data = await contactRef.current?.triggerFormSubmit();

    if (!data) {
      return false;
    }

    const canAdvance = (await onContactNext?.(data)) ?? true;

    if (!canAdvance) {
      return false;
    }

    setContactData(data);
  }, [onContactNext]);

  const onConfirmAttributes = useCallback(async () => {
    setHasSubmittedAttributes(true);

    const attributes = await attributesRef.current?.triggerFormSubmit();

    if (!attributes) {
      return false;
    }

    // The vendor, the number and the revision ride along with the contact: all are answered on the
    // contact step, and the finalize needs the user's answers, not the prefill the defaults hold.
    const data = (
      withContactStep
        ? {
            ...formDefaultValues,
            contact: contactData?.contact,
            vendorLocation: contactData?.vendorLocation,
            externalNumber: contactData?.externalNumber,
            revisionNumber: contactData?.revisionNumber,
          }
        : formDefaultValues
    ) as IImportVendorQuoteForm;

    // Only an explicit false is a failure — the re-import path resolves void and must still close.
    if (false === (await onConfirm?.(data, attributes, drawerImportActionsRef.current))) {
      return false;
    }
  }, [withContactStep, contactData, formDefaultValues, onConfirm]);

  const steps = useMemo<IStep[]>(() => {
    const attributesStep: IStep = {
      type: ImportWizardStep.ATTRIBUTES,
      title: t('titleAttributes'),
      content: (
        <ImportAttributes
          ref={attributesRef}
          config={attributesConfig}
          hasSubmitted={hasSubmittedAttributes}
          draft={draftAttributes}
          isFirstImport={isFirstImport}
        />
      ),
      previousButton: {
        options: { onClick: onBackFromAttributes },
      },
      nextButton: {
        text: t('buttonLabelImportVendorQuote'),
        options: { onClick: onConfirmAttributes },
      },
    };

    if (!withContactStep) {
      return [attributesStep];
    }

    return [
      {
        type: ImportWizardStep.CONTACT,
        title: t('titleImportVendorQuote'),
        content: (
          <ImportContact
            ref={contactRef}
            defaultValues={contactData ?? formDefaultValues}
            disabled={disabled}
            showVendorLocation={withVendorLocation}
            isVendorLocked={isVendorLocked}
            isVendorMapped={isVendorMapped}
            isVendorMappingRemembered={isVendorMappingRemembered}
            showVendorQuoteNumber={withVendorQuoteNumber}
            isVendorQuoteNumberLocked={isVendorQuoteNumberLocked}
            showVendorQuoteRevision={withVendorQuoteRevision}
            isContactLocked={isContactLocked}
            suggestedContactIdRef={suggestedContactIdRef}
            // Only the very first mount shows the BE prefill; once the user has been through the
            // step, `contactData` holds their own picks and the step is remounted with those.
            isContactPrefilled={!contactData}
          />
        ),
        nextButton: {
          text: t('buttonLabelNext'),
          options: { onClick: onNextFromContact },
        },
      },
      attributesStep,
    ];
  }, [
    t,
    withContactStep,
    withVendorLocation,
    isVendorLocked,
    isVendorMapped,
    isVendorMappingRemembered,
    withVendorQuoteNumber,
    isVendorQuoteNumberLocked,
    withVendorQuoteRevision,
    isContactLocked,
    attributesConfig,
    isFirstImport,
    hasSubmittedAttributes,
    contactData,
    draftAttributes,
    formDefaultValues,
    disabled,
    onConfirmAttributes,
    onNextFromContact,
    onBackFromAttributes,
  ]);

  if (!isOpen) {
    return null;
  }

  return (
    <StepsProvider steps={steps} onClose={handleClose}>
      <Steps
        title={t('titleImportVendorQuote')}
        // Single-step drawers hide the "Step N of M" line, so label the step in the panel header.
        subtitle={withContactStep ? undefined : t('titleAttributes')}
        isOpen={isOpen}
        fullWidth={false}
      />
    </StepsProvider>
  );
};

// Own notification provider: the wizard's banners (validation, the finalize's loading / error)
// must not share the page's single notification slot with an enclosing panel (Customer Quote >
// Costing renders this inside a SlidingPanelContent) or a sibling drawer, which would paint them
// twice and let unrelated loading states freeze this wizard's buttons.
export const ImportVendorQuoteSteps = (props: IProps) => (
  <NotificationProvider>
    <ImportVendorQuoteStepsContent {...props} />
  </NotificationProvider>
);
